import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  BookOpen,
  FileText,
  Layers,
  PenTool,
  ChevronRight,
  GraduationCap,
  Eye,
  Info,
  Lock,
  Play
} from 'lucide-react'

interface ContentStats {
  units: number
  lessons: number
  contents: number
  exercises: number
  workshops: number
}

export default function ContentManagement() {
  const [stats, setStats] = useState<ContentStats>({
    units: 0,
    lessons: 0,
    contents: 0,
    exercises: 0,
    workshops: 0
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const [
        { count: unitsCount },
        { count: lessonsCount },
        { count: contentsCount },
        { count: exercisesCount },
        { count: workshopsCount }
      ] = await Promise.all([
        supabase.from('units').select('*', { count: 'exact', head: true }),
        supabase.from('lessons').select('*', { count: 'exact', head: true }),
        supabase.from('contents').select('*', { count: 'exact', head: true }),
        supabase.from('exercises').select('*', { count: 'exact', head: true }),
        supabase.from('unit_workshops').select('*', { count: 'exact', head: true })
      ])

      setStats({
        units: unitsCount || 0,
        lessons: lessonsCount || 0,
        contents: contentsCount || 0,
        exercises: exercisesCount || 0,
        workshops: workshopsCount || 0
      })
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const contentTypes = [
    {
      title: 'Unidades',
      description: 'Navegar el curso en modo previsualización',
      icon: Play,
      href: '/teacher/preview/units',
      color: 'bg-blue-100 text-blue-600',
      stats: `${stats.units} unidades`,
      isPreview: true
    },
    {
      title: 'Lecciones',
      description: 'Explorar las lecciones de cada unidad',
      icon: BookOpen,
      href: '/teacher/content/lessons',
      color: 'bg-green-100 text-green-600',
      stats: `${stats.lessons} lecciones`,
      isPreview: false
    },
    {
      title: 'Contenido',
      description: 'Ver teoría, videos y audio por lección',
      icon: FileText,
      href: '/teacher/content/contents',
      color: 'bg-orange-100 text-orange-600',
      stats: `${stats.contents} contenidos`,
      isPreview: false
    },
    {
      title: 'Ejercicios',
      description: 'Revisar los ejercicios por habilidad',
      icon: PenTool,
      href: '/teacher/content/exercises',
      color: 'bg-purple-100 text-purple-600',
      stats: `${stats.exercises} ejercicios`,
      isPreview: false
    }
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-blue-600" />
          Contenido del Curso
        </h1>
        <p className="text-gray-600">
          Explora el material educativo de la plataforma
        </p>
      </div>

      {/* Notice - Read Only */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Info className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-medium text-blue-900">Modo de solo lectura</h3>
          <p className="text-sm text-blue-700 mt-1">
            Como profesor, puedes ver todo el contenido del curso pero la creación y edición
            está reservada para los administradores. Si necesitas hacer cambios, contacta al
            administrador del sistema.
          </p>
        </div>
      </div>

      {/* Grid de tipos de contenido */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {contentTypes.map((type) => (
          <Link
            key={type.href}
            to={type.href}
            className={`bg-white rounded-xl border p-5 hover:shadow-md transition-all group ${
              type.isPreview ? 'hover:border-green-400 border-green-200' : 'hover:border-blue-300'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className={`w-12 h-12 ${type.color} rounded-xl flex items-center justify-center`}>
                <type.icon className="w-6 h-6" />
              </div>
              <div className="flex items-center gap-2">
                {type.isPreview ? (
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">Preview</span>
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </div>
            </div>
            <h3 className="font-semibold mt-4 text-gray-900">{type.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{type.description}</p>
            <div className="mt-3 text-xs text-blue-600 font-medium">{type.stats}</div>
          </Link>
        ))}
      </div>

      {/* Estadísticas del contenido */}
      <div className="bg-white rounded-xl border p-6">
        <h2 className="font-semibold mb-4 text-gray-900">Resumen del Contenido</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="flex justify-center mb-2">
              <Layers className="w-6 h-6 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.units}</p>
            <p className="text-xs text-gray-500">Unidades</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="flex justify-center mb-2">
              <BookOpen className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.lessons}</p>
            <p className="text-xs text-gray-500">Lecciones</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="flex justify-center mb-2">
              <FileText className="w-6 h-6 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.contents}</p>
            <p className="text-xs text-gray-500">Contenidos</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="flex justify-center mb-2">
              <PenTool className="w-6 h-6 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.exercises}</p>
            <p className="text-xs text-gray-500">Ejercicios</p>
          </div>
        </div>
      </div>

      {/* Estructura del curso */}
      <div className="bg-white rounded-xl border p-6">
        <h2 className="font-semibold mb-4 text-gray-900">Estructura del Curso</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold">
              4
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Unidades</p>
              <p className="text-sm text-gray-500">Cada unidad representa una semana de estudio</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg ml-8">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 font-bold">
              4
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Lecciones por unidad</p>
              <p className="text-sm text-gray-500">Contenido tematico organizado</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg ml-16">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 font-bold">
              4
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Contenidos por lección</p>
              <p className="text-sm text-gray-500">Teoría, vocabulario, gramática y práctica</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg ml-24">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-bold">
              12
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">Ejercicios por contenido</p>
              <p className="text-sm text-gray-500">3 por habilidad: habla, escucha, escritura, lectura</p>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions Info */}
      <div className="bg-gray-50 border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="w-5 h-5 text-gray-400" />
          <h2 className="font-semibold text-gray-700">Permisos de Profesor</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <p className="font-medium text-green-700">Puedes:</p>
            <ul className="space-y-1 text-gray-600">
              <li className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-500" />
                Ver todo el contenido del curso
              </li>
              <li className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-500" />
                Ver el progreso de tus estudiantes
              </li>
              <li className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-500" />
                Marcar asistencia en talleres
              </li>
              <li className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-500" />
                Gestionar tus propios talleres
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-medium text-red-700">No puedes:</p>
            <ul className="space-y-1 text-gray-600">
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-red-500" />
                Crear o editar unidades
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-red-500" />
                Crear o editar lecciones
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-red-500" />
                Crear o editar ejercicios
              </li>
              <li className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-red-500" />
                Modificar exámenes
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
